<template>
  <!-- Manual creation-date edit for ONE transaction (MONETARY_UPDATE_DATE).
       Used by the monetaries table and the client modal's monetary table. -->
  <b-modal v-model="show" size="md" centered :title="$t('monetary.editDate')" @hidden="reset">
    <div v-if="tx" class="mdm">
      <div class="mdm__tx">
        <b-badge variant="light-primary">#{{ tx.id }}</b-badge>
        <b-badge :variant="tx.type === 'WITHDRAWAL' ? 'light-danger' : 'light-success'" class="ml-25">{{ tx.type }}</b-badge>
        <span class="mdm__amount ml-50">{{ convertToEURO(tx.amount || 0) }}</span>
      </div>
      <div class="mdm__current">
        <small class="text-muted d-block">{{ $t('columns.creationTime') }}</small>
        <span>{{ tx.registrationTime | momentDateTime }}</span>
        <small v-if="tx.originalRegistrationTime" class="text-warning d-block mt-25">
          <i class="fa-solid fa-clock-rotate-left mr-25"></i>{{ $t('monetary.originalDate') }}: {{ tx.originalRegistrationTime | momentDateTime }}
        </small>
      </div>
      <label class="mdm__label">{{ $t('monetary.newDate') }}</label>
      <div class="mdm__pickers">
        <!-- Short date format so the picker button stays on ONE line -->
        <b-form-datepicker v-model="date" :locale="$i18n.locale" :max="today" class="mdm__date"
                           :date-format-options="{ year: 'numeric', month: '2-digit', day: '2-digit' }"/>
        <b-form-timepicker v-model="time" :locale="$i18n.locale" class="mdm__time"/>
      </div>
      <p class="mdm__hint text-muted">{{ $t('monetary.editDateHint') }}</p>
    </div>
    <template #modal-footer>
      <b-button variant="flat-secondary" size="sm" @click="show = false">{{ $t('operations.cancel') }}</b-button>
      <b-button variant="primary" size="sm" :disabled="saving || !date || !time" @click="save">
        <b-spinner v-if="saving" small class="mr-25"/>{{ $t('operations.save') }}
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import { mapActions } from 'vuex';
import mixinBase from '@/components/mixin/mixinBase';
import mixinNotifications from '@/components/mixin/mixinNotifications';

export default {
  name: 'MonetaryDateModal',
  mixins: [mixinBase, mixinNotifications],
  data: () => ({
    show: false,
    tx: null,
    date: null,
    time: null,
    saving: false,
  }),
  computed: {
    today() { return this.$moment().format('YYYY-MM-DD'); },
  },
  methods: {
    ...mapActions('monetary', ['updateMonetaryDate']),
    open(tx) {
      this.tx = tx;
      const current = tx && tx.registrationTime ? this.$moment(tx.registrationTime) : this.$moment();
      this.date = current.format('YYYY-MM-DD');
      this.time = current.format('HH:mm:ss');
      this.show = true;
    },
    reset() {
      this.tx = null;
      this.date = null;
      this.time = null;
      this.saving = false;
    },
    save() {
      if (!this.tx || !this.date || !this.time) return;
      // Backend accepts 'yyyy-MM-dd HH:mm' (and with seconds)
      const registrationTime = `${this.date} ${this.time.substring(0, 5)}`;
      this.saving = true;
      this.updateMonetaryDate({ id: this.tx.id, registrationTime })
          .then(saved => {
            this.showSuccessNotification(this.$t('monetary.dateSaved'));
            this.$emit('saved', saved);
            this.show = false;
          })
          .catch(err => {
            this.showErrorNotification(this.$t('monetary.editDate'), this.getErrorDescription(err));
          })
          .finally(() => { this.saving = false; });
    },
  },
};
</script>

<style lang="scss" scoped>
.mdm {
  &__tx { display: flex; align-items: center; margin-bottom: 0.6rem; }
  &__amount { font-weight: 700; }
  &__current { margin-bottom: 0.9rem; font-size: 0.9rem; }
  &__label { font-size: 0.8rem; font-weight: 700; margin-bottom: 0.3rem; display: block; }
  &__pickers { display: flex; gap: 0.6rem; }
  &__date { flex: 1 1 58%; min-width: 0; }
  &__time { flex: 1 1 42%; min-width: 0; }
  &__pickers ::v-deep .btn { white-space: nowrap; }
  &__hint { font-size: 0.75rem; margin: 0.6rem 0 0; }
}
</style>
